import { useState } from 'react';
import { Settings, RotateCcw, LogOut, LogIn, ChevronDown, Wifi, Shield, Trash2, Download, UserCircle, Monitor, Smartphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DeleteAccountDialog } from '@/components/onboarding/DeleteAccountDialog';
import { callRpc } from '@/lib/supabaseTyped';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { isForcingDesktop, setForcingDesktop } from '@/hooks/use-mobile';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { usePlan } from '@/hooks/usePlan';
import { Badge } from '@/components/ui/badge';
import { BRAND_NAVY } from '@/constants/brandColors';
import { useOnboarding } from '@/components/onboarding/useOnboarding';
import type {
  ExperienceLevel,
  Interest,
  UseCase,
  UserRole,
} from '@/components/onboarding/types';

const T = {
  sv: {
    signIn: 'Logga in',
    realtimeSync: 'Realtidssynk aktiv',
    adminDashboard: 'Admin Dashboard',
    profile: 'Profil',
    prefs: 'Preferenser',
    experience: 'Erfarenhetsnivå',
    role: 'Roll',
    useCase: 'Vad bygger du',
    interests: 'Intressen',
    restart: 'Starta om onboarding',
    myProfile: 'Min profil',
    signOut: 'Logga ut',
    logOutLong: 'Logga ut',
    beginner: 'Nybörjare',
    experienced: 'Erfaren',
    power: 'Power-user',
    designer: 'Designer',
    developer: 'Utvecklare',
    business: 'Business',
    other: 'Annat',
    // 7 use-cases matching WorkflowWizardDialog templates
    itInfra: 'IT Infrastructure',
    businessProcess: 'Business Process',
    orgChart: 'Organization Chart',
    decisionTree: 'Decision Tree',
    mindMap: 'Mind Map',
    projectPlanning: 'Project Planning',
    sandbox: 'Advanced / Sandbox',
    ai: 'AI-assistent',
    threeD: '3D-vy',
    multiSheet: 'Multi-sheet',
    exportShare: 'Export & dela',
    shortcuts: 'Kortkommandon',
  },
  en: {
    signIn: 'Sign in',
    realtimeSync: 'Realtime sync active',
    adminDashboard: 'Admin Dashboard',
    profile: 'Profile',
    prefs: 'Preferences',
    experience: 'Experience level',
    role: 'Role',
    useCase: 'What are you building',
    interests: 'Interests',
    restart: 'Restart onboarding',
    myProfile: 'My Profile',
    signOut: 'Sign out',
    beginner: 'Beginner',
    experienced: 'Experienced',
    power: 'Power user',
    designer: 'Designer',
    developer: 'Developer',
    business: 'Business',
    other: 'Other',
    itInfra: 'IT Infrastructure',
    businessProcess: 'Business Process',
    orgChart: 'Organization Chart',
    decisionTree: 'Decision Tree',
    mindMap: 'Mind Map',
    projectPlanning: 'Project Planning',
    sandbox: 'Advanced / Sandbox',
    ai: 'AI assistant',
    threeD: '3D view',
    multiSheet: 'Multi-sheet',
    exportShare: 'Export & share',
    shortcuts: 'Shortcuts',
  },
};

export interface ProfileMenuProps {
  /** Called from the signed-out trigger button. */
  onLogin?: () => void;
}

export function ProfileMenu({ onLogin }: ProfileMenuProps = {}) {
  const { user, isAuthenticated, isLoading, signOut } = useAuth();
  const { isAdmin } = useAdminAuth();
  const { tier } = usePlan();
  const ctx = useOnboarding();
  const t = T[ctx.lang];
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleDataExport = async () => {
    setExporting(true);
    try {
      const { data, error } = await callRpc<Record<string, unknown>>('self_data_export', {});
      if (error) throw error;
      const blob = new Blob([JSON.stringify(data ?? {}, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `flowweaver-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Dataexport klar', description: 'Filen sparades till din nedladdningsmapp.' });
    } catch (e) {
      toast({
        title: 'Export misslyckades',
        description: e instanceof Error ? e.message : String(e),
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  if (isLoading) return null;

  // Signed-out: just a "Sign in" button. Onboarding context is still usable
  // but the preferences sub-menu is hidden.
  if (!isAuthenticated || !user) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={onLogin}
        className="text-white hover:opacity-90 rounded-md gap-2"
        style={{ backgroundColor: '#16a34a' }}
        data-testid="topbar-login"
      >
        <LogIn className="w-4 h-4" />
        <span className="hidden sm:inline">{t.signIn}</span>
      </Button>
    );
  }

  const prefs = ctx.progress?.preferences ?? null;
  const email = user.email ?? user.id.slice(0, 8);
  const initials = (user.email ?? user.id).split('@')[0].slice(0, 2).toUpperCase();

  const toggleInterest = (i: Interest) => {
    const current = prefs?.interests ?? [];
    const next = current.includes(i) ? current.filter(x => x !== i) : [...current, i];
    ctx.updatePreferences({ interests: next });
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:opacity-90 text-sm"
          style={{ backgroundColor: BRAND_NAVY, color: 'white' }}
          data-guide="topbar-profile"
          data-testid="topbar-user-menu"
        >
          <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">
            {initials}
          </div>
          <span className="hidden sm:inline text-xs truncate max-w-[140px]">{email}</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel>
          <div className="flex items-center gap-2">
            <div className="font-medium">{t.profile}</div>
            <Badge variant="outline" className="text-[9px] capitalize" data-testid="profile-tier-badge">
              {tier}
            </Badge>
          </div>
          <div className="text-xs font-normal text-muted-foreground truncate">{email}</div>
          <div className="flex items-center gap-1.5 mt-1.5">
            <Wifi className="w-3 h-3 text-green-500" />
            <span className="text-[11px] font-normal text-muted-foreground">{t.realtimeSync}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {isAdmin && (
          <>
            <DropdownMenuItem onClick={() => (window.location.href = '/admin')}>
              <Shield className="w-4 h-4 mr-2" />
              {t.adminDashboard}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem asChild>
          <Link to="/billing" className="cursor-pointer" data-testid="profile-my-profile">
            <UserCircle className="w-4 h-4 mr-2" />
            {t.myProfile}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Settings className="w-4 h-4 mr-2" />
            {t.prefs}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-56">
            <DropdownMenuLabel className="text-xs text-muted-foreground uppercase">{t.experience}</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={prefs?.experience ?? ''}
              onValueChange={(v) => ctx.updatePreferences({ experience: v as ExperienceLevel })}
            >
              <DropdownMenuRadioItem value="beginner">{t.beginner}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="experienced">{t.experienced}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="power-user">{t.power}</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-xs text-muted-foreground uppercase">{t.role}</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={prefs?.role ?? ''}
              onValueChange={(v) => ctx.updatePreferences({ role: v as UserRole })}
            >
              <DropdownMenuRadioItem value="designer">{t.designer}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="developer">{t.developer}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="business">{t.business}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="other">{t.other}</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-xs text-muted-foreground uppercase">{t.useCase}</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={prefs?.useCase ?? ''}
              onValueChange={(v) => ctx.updatePreferences({ useCase: v as UseCase })}
            >
              <DropdownMenuRadioItem value="it-infrastructure">{t.itInfra}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="business-process">{t.businessProcess}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="organization-chart">{t.orgChart}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="decision-tree">{t.decisionTree}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="mind-map">{t.mindMap}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="project-planning">{t.projectPlanning}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="sandbox">{t.sandbox}</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />

            <DropdownMenuLabel className="text-xs text-muted-foreground uppercase">{t.interests}</DropdownMenuLabel>
            <DropdownMenuCheckboxItem
              checked={prefs?.interests.includes('ai-assistant') ?? false}
              onCheckedChange={() => toggleInterest('ai-assistant')}
            >{t.ai}</DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={prefs?.interests.includes('3d-view') ?? false}
              onCheckedChange={() => toggleInterest('3d-view')}
            >{t.threeD}</DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={prefs?.interests.includes('multi-sheet') ?? false}
              onCheckedChange={() => toggleInterest('multi-sheet')}
            >{t.multiSheet}</DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={prefs?.interests.includes('export-share') ?? false}
              onCheckedChange={() => toggleInterest('export-share')}
            >{t.exportShare}</DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={prefs?.interests.includes('shortcuts') ?? false}
              onCheckedChange={() => toggleInterest('shortcuts')}
            >{t.shortcuts}</DropdownMenuCheckboxItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuItem onClick={() => ctx.resetAll()}>
          <RotateCcw className="w-4 h-4 mr-2" />
          {t.restart}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleDataExport}
          disabled={exporting}
          data-testid="profile-data-export"
        >
          <Download className="w-4 h-4 mr-2" />
          {exporting ? 'Exporterar…' : 'Exportera mina data (GDPR)'}
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/privacy" className="cursor-pointer">
            <Shield className="w-4 h-4 mr-2" />
            Integritetspolicy
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/terms" className="cursor-pointer">
            <Settings className="w-4 h-4 mr-2" />
            Användarvillkor
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {/* #41: opt-out av mobil-shell. Toggle uppdaterar useIsMobile via
            CustomEvent — ingen reload behövs. */}
        <DropdownMenuItem
          onClick={() => setForcingDesktop(!isForcingDesktop())}
          data-testid="profile-toggle-desktop-mode"
        >
          {isForcingDesktop() ? (
            <>
              <Smartphone className="w-4 h-4 mr-2" />
              Visa mobilversion
            </>
          ) : (
            <>
              <Monitor className="w-4 h-4 mr-2" />
              Visa skrivbordsversion
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => signOut()}>
          <LogOut className="w-4 h-4 mr-2" />
          {t.signOut}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setDeleteOpen(true)}
          className="text-destructive focus:text-destructive focus:bg-destructive/10"
          data-testid="profile-delete-account"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Radera konto
        </DropdownMenuItem>
      </DropdownMenuContent>
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
    </DropdownMenu>
  );
}
